<template>
  <div
    class="material-option__content columns is-multiline is-gapless"
    :class="{
      'is-disabled': isDisabled,
      'is-loading': isLoading,
      'is-selectable': selectable,
    }"
  >
    <div class="material-option__image column is-2-mobile is-flex is-align-items-center" >
      <img :src="`${thumbnailBaseUrl}${tempColor.thumbnail}`" :alt="`${printMode.name}`" />
    </div>
    <div class="material-option__data column is-10">
      <div class="material-option__meta pl-2 pr-1 py-1">
        <span class="material-option__label">{{ printMode.name }}</span>
        <span class="material-option__description">
          {{ printMode.features.description }}
         </span>
      </div>
      <span class="ml-2 pr-1 material-option__description">
        <b-skeleton v-if="isLoading" width="100%" class="ml-2 pr-1" />
        <template v-else-if="tempColor.pricing">
          {{ $t("orderDetails.labels.deliveryAndProductionTimeShort") }}:
          {{
            $tc("checkout.productionTimeDays", tempColor.pricing.productionTime)
          }}
        </template>
      </span>
    </div>
    <div
      class="
        material-option__color-select
        column is-full
        is-flex is-justify-content-space-between
      "
      :class="{
        'is-flex-direction-row-reverse is-align-items-center': [
          viewports.MOBILE.name,
        ].includes(currentViewport),
        'is-flex-direction-column': ![viewports.MOBILE.name].includes(
          currentViewport
        ),
      }"
      tabindex="0"
      @focusout="isOpen = false"
    >
      <print-mode-price
        :is-loading="isLoading"
        :print-mode="printMode"
        :current-color="selectedColor || cheapestColor"
      />
      <div class="material-option__info mr-2">
        <b-icon
          icon="information-outline"
          :type="iconHover ? 'is-primary-dark' : 'is-primary'"
          @mouseenter.native="
            iconHover = true;
            $emit('mouseleave');
          "
          @mouseleave.native="iconHover = false"
          @click.native="showPrintModeDetails = true"
        />
      </div>
    </div>
    <b-sidebar
      v-model="showPrintModeDetails"
      :fullheight="true"
      class="is-vcentered"
      v-if="![viewports.MOBILE.name].includes(currentViewport)"
    >
      <div class="p-6">
        <div class="columns">
          <div class="column has-text-left">
            <b-button
              type="is-primary-dark is-ghost"
              class="p-0"
              icon-left="close"
              @click="showPrintModeDetails = false"
            >
              {{ $t("checkout.cart.actionLabels.close") }}
            </b-button>
          </div>
        </div>
        <print-mode-detail
          :print-mode-id="this.printMode.id"
        ></print-mode-detail>
      </div>
    </b-sidebar>
    <b-modal
      v-else
      v-model="showPrintModeDetails"
      full-screen
      :can-cancel="false"
    >
      <div class="p-6">
        <div class="columns">
          <div class="column has-text-left p-0">
            <b-button
              type="is-primary-dark is-ghost"
              class="p-0"
              icon-left="arrow-left"
              @click="showPrintModeDetails = false"
            >
              {{ $t("checkout.cart.actionLabels.goBack") }}
            </b-button>
          </div>
          <print-mode-detail
            :print-mode-id="this.printMode.id"
            :can-cancel="false"
          ></print-mode-detail>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import PrintModeColorSelect from "./PrintModeColorSelect";
import { mapGetters } from "vuex";
import { eventBus } from "../../../events/event-bus";
import { viewports } from "../../../store/constants/viewports";
import PrintModePrice from "./PrintModePrice";
import PrintModeDetail from "./PrintModeDetail";

export default {
  name: "PrintModeOption",
  components: { PrintModePrice, PrintModeColorSelect, PrintModeDetail },
  data() {
    return {
      thumbnailBaseUrl: process.env.VUE_APP_FULFILLMENT_API_BASE_URL,
      isLoading: false,
      iconHover: false,
      showPrintModeDetails: false,
      viewports,
      calculationResults: {
        price: 0,
        productionTime: 0,
        isPrintable: true,
        tooLarge: false,
        tooSmall: false,
      },
    };
  },
  props: {
    printMode: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      required: true,
    },
    updatePricing: {
      type: Boolean,
      required: false,
      default: true,
    },
    selectable: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  computed: {
    ...mapGetters(["currentCartItem", "currentViewport", "current3DFile"]),
    selectedColor: {
      get() {
        if (this.currentCartItem.printModeId !== this.printMode.id)
          return this.cheapestColor;

        let surfaceFinish = this.printMode.surfaceFinishes.find(
          (surfaceFinish) =>
            surfaceFinish.id === this.currentCartItem.surfaceFinishId
        );

        if (!surfaceFinish) return this.cheapestColor;
        let color = surfaceFinish.colors.find(
          (color) => color.id === this.currentCartItem.color.id
        );

        if (color) return color;

        return this.cheapestColor;
      },
      set(value) {
        this.$store.dispatch("updateCurrentCartItem", {
          color: value,
        });
      },
    },
    cheapestColor() {
      let color = this.printMode.surfaceFinishes[0].colors[0];
      this.printMode.surfaceFinishes.forEach((surfaceFinish) => {
        surfaceFinish.colors.forEach((finishColor) => {
          try {
            if (
              finishColor.pricing.calculatedTaxes.netPrice <
              color.pricing.calculatedTaxes.netPrice
            ) {
              color = finishColor;
            }
          } catch (e) {
            //do nothing
          }
        });
      });
      return color;
    },
    tempColor() {
      return this.selectedColor;
    },
    isDisabled() {
      let containsSelectedFinish = false;
      let isPrintable = this.tempColor.pricing
        ? this.tempColor.pricing.printable
        : true;

      return this.disabled || !isPrintable;
    },
  },
  watch: {
    isSelected(value) {
      if (value === true) {
        this.selectedColor = this.tempColor;
      }
    },
  },
  methods: {
    async calculatePricing() {
      this.isLoading = true;
      if (this.updatePricing) {
        await this.$store.dispatch("fetchPrintModePrices", this.printMode.id);
        this.isLoading = false;
      } else {
        setTimeout(() => {
          this.isLoading = false;
        }, 500);
      }
    },
  },
  mounted() {
    eventBus.$on("cartItemPricingInvalid", this.calculatePricing);
  },
  beforeDestroy() {
    eventBus.$off("cartItemPricingInvalid", this.calculatePricing);
  },
};
</script>
